/**
 * Der Kontext eines Klick-Ereignisses — Ziel **und**, bei Klicks nach außen, die Zieldomain.
 *
 * Bis zum 18.09.2026 trug ein Anmelde-Klick nur sein Ziel (`anmeldung`, `link:REGISTRATION`).
 * Damit ließ sich nicht beantworten, welche Anmeldeplattformen wir eigentlich beliefern — die
 * Domain stand nur am Event, und ein Event kann mehrere Anmeldelinks haben oder seinen Link
 * seither gewechselt haben. Deshalb hängt der Klick die Domain jetzt selbst an:
 *
 *     anmeldung>racement.de
 *     link:REGISTRATION>my.raceresult.com
 *
 * Das `>` kommt in keinem bestehenden Kontext vor, und `link:TYP` bringt selbst schon einen
 * Doppelpunkt mit — deshalb kein zweiter `:`. Jeder Leser, den nur das Ziel interessiert,
 * nimmt [clickContextBase]; Bestandszeilen ohne Domain bleiben unverändert gültig.
 */

const DOMAIN_SEPARATOR = '>';

/**
 * Die Domain einer (evtl. schema-losen) URL, klein geschrieben und ohne `www.`.
 *
 * Bewusst von Hand geparst statt über `new URL()`: dieselbe Funktion läuft in der App, wo `URL`
 * je nach Engine unvollständig ist, und ein Tracking-Aufruf darf nie werfen.
 */
export function linkDomain(url?: string | null): string | null {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return null;
  // Schema und Protokoll-relativen Anfang abschneiden; mailto:/tel: haben keine Domain.
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(trimmed)?.[1]?.toLowerCase();
  if (scheme && scheme !== 'http' && scheme !== 'https') return null;
  let rest = trimmed.replace(/^[a-z][a-z0-9+.-]*:/i, '').replace(/^\/\//, '');
  // Pfad, Query und Fragment weg — was zuerst kommt.
  rest = rest.split('/')[0].split('?')[0].split('#')[0];
  // Anmeldedaten (user:pass@host) und Port weg.
  const at = rest.lastIndexOf('@');
  if (at >= 0) rest = rest.slice(at + 1);
  rest = rest.split(':')[0].trim().toLowerCase();
  if (!rest || !rest.includes('.')) return null;
  return rest.replace(/^www\./, '');
}

/**
 * Der Kontext für einen Klick nach außen: Ziel, und die Zieldomain sofern erkennbar.
 * Ohne erkennbare Domain bleibt es beim bisherigen Wert.
 */
export function outboundClickContext(target: string, url?: string | null): string {
  const domain = linkDomain(url);
  return domain ? `${target}${DOMAIN_SEPARATOR}${domain}` : target;
}

/** Das Ziel ohne Domain — die Form, in der Kontexte vor dem 18.09.2026 gespeichert wurden. */
export function clickContextBase(context?: string | null): string {
  if (!context) return '';
  const cut = context.indexOf(DOMAIN_SEPARATOR);
  return cut < 0 ? context : context.slice(0, cut);
}

/** Die Zieldomain eines Kontexts, oder null bei Bestandszeilen ohne Domain. */
export function clickContextDomain(context?: string | null): string | null {
  if (!context) return null;
  const cut = context.indexOf(DOMAIN_SEPARATOR);
  return cut < 0 ? null : context.slice(cut + 1) || null;
}
